import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Clock, ChevronRight } from 'lucide-react';
import './MarketTicker.css';

// Cache keys
const TICKER_CACHE_KEY = 'wassla.marketTicker.v1';
const TICKER_CACHE_TTL = 30 * 60 * 1000; // 30 minutes

// Default prices (fallback when API unavailable)
const DEFAULT_PRICES = [
  { cropSlug: 'sesame', cropNameAr: 'سمسم', cropNameEn: 'Sesame', avgPrice: 50000, unit: 'جوال', unitEn: 'bag' },
  { cropSlug: 'sorghum', cropNameAr: 'ذرة', cropNameEn: 'Sorghum', avgPrice: 18000, unit: 'قنطار', unitEn: 'quintar' },
  { cropSlug: 'groundnuts', cropNameAr: 'فول سوداني', cropNameEn: 'Groundnuts', avgPrice: 35000, unit: 'جوال', unitEn: 'bag' },
  { cropSlug: 'onions', cropNameAr: 'بصل', cropNameEn: 'Onions', avgPrice: 20000, unit: 'جوال', unitEn: 'bag' },
  { cropSlug: 'wheat', cropNameAr: 'قمح', cropNameEn: 'Wheat', avgPrice: 22000, unit: 'قنطار', unitEn: 'quintar' },
];

function MarketTicker({ state = 'all', onTickerClick }) {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || isArabic;
  
  const [prices, setPrices] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  // Format price with SDG
  const formatPrice = (price) => {
    return new Intl.NumberFormat(isArabic ? 'ar-SD' : 'en-SD').format(price);
  };

  // Format time
  const formatTime = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleTimeString(isArabic ? 'ar-SD' : 'en-SD', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Load from cache
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(TICKER_CACHE_KEY);
      if (cached) {
        const { data, timestamp, cachedState } = JSON.parse(cached);
        const age = Date.now() - timestamp;
        
        // Use cache if fresh and same state
        if (age < TICKER_CACHE_TTL && cachedState === state) {
          setPrices(data);
          setLastUpdated(new Date(timestamp));
          return true;
        }
      }
    } catch (e) {
      console.error('Cache read error:', e);
    }
    return false;
  }, [state]);

  // Save to cache
  const saveToCache = useCallback((data) => {
    try {
      localStorage.setItem(TICKER_CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now(),
        cachedState: state
      }));
    } catch (e) {
      console.error('Cache write error:', e);
    }
  }, [state]);

  // Fetch prices from API
  const fetchPrices = useCallback(async () => {
    // Try cache first
    if (loadFromCache()) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('date', 'today');
      if (state && state !== 'all') {
        params.append('state', state);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/market-prices?${params.toString()}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.prices && data.prices.length > 0) {
          setPrices(data.prices);
          setLastUpdated(new Date());
          saveToCache(data.prices);
        } else {
          // Use defaults if no data
          setPrices(DEFAULT_PRICES);
          setLastUpdated(new Date());
        }
      } else {
        // Use defaults on error
        setPrices(DEFAULT_PRICES);
        setLastUpdated(new Date());
      }
    } catch (error) {
      console.error('Failed to fetch market prices:', error);
      // Use defaults on error
      setPrices(DEFAULT_PRICES);
      setLastUpdated(new Date());
    } finally {
      setLoading(false);
    }
  }, [state, loadFromCache, saveToCache]);

  // Initial fetch and refresh interval
  useEffect(() => {
    fetchPrices();
    
    // Refresh every 10 minutes
    const interval = setInterval(fetchPrices, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchPrices]);

  // Generate ticker text
  const generateTickerItems = () => {
    return prices.map((price, index) => (
      <span key={`${price.cropSlug}-${index}`} className="ticker-item">
        <span className="crop-name">
          {isArabic ? price.cropNameAr : price.cropNameEn}
        </span>
        <span className="crop-price">
          {formatPrice(price.avgPrice)} SDG/{isArabic ? price.unit : price.unitEn}
        </span>
        <span className="ticker-divider">|</span>
      </span>
    ));
  };

  if (loading && prices.length === 0) {
    return (
      <div className="market-ticker loading" data-testid="market-ticker">
        <div className="ticker-loading">
          <TrendingUp size={16} />
          <span>{isArabic ? 'جارٍ تحميل الأسعار...' : 'Loading prices...'}</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="market-ticker" 
      onClick={onTickerClick}
      data-testid="market-ticker"
      role="button"
      tabIndex={0}
    >
      <div className="ticker-icon">
        <TrendingUp size={16} />
      </div>
      
      <div className="ticker-scroll-container">
        <div className="ticker-scroll" style={{ animationDirection: isRTL ? 'reverse' : 'normal' }}>
          {generateTickerItems()}
          {/* Duplicate for seamless loop */}
          {generateTickerItems()}
        </div>
      </div>
      
      <div className="ticker-meta">
        {lastUpdated && (
          <span className="ticker-time">
            <Clock size={12} />
            {formatTime(lastUpdated)}
          </span>
        )}
        <ChevronRight size={16} className="ticker-arrow" />
      </div>
    </div>
  );
}

export default MarketTicker;
